"use client";

import { type ReactNode, useMemo, useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";

/* ------------------------------------------------------------------ */
/*  Confused word groups (module level) — each group is a pair or     */
/*  triple of words that are commonly mixed up, plus a one-line rule. */
/* ------------------------------------------------------------------ */

interface ConfusedGroup {
  words: string[];
  rule: string;
}

const CONFUSED_GROUPS: ConfusedGroup[] = [
  {
    words: ["their", "there", "they're"],
    rule: "their = possessive (their book); there = place (over there); they're = they are.",
  },
  {
    words: ["its", "it's"],
    rule: "its = possessive (its cover); it's = it is or it has.",
  },
  {
    words: ["affect", "effect"],
    rule: "affect is usually the verb (to influence); effect is usually the noun (the result).",
  },
  {
    words: ["then", "than"],
    rule: "then = time or sequence (first this, then that); than = comparison (bigger than).",
  },
  {
    words: ["your", "you're"],
    rule: "your = possessive (your paper); you're = you are.",
  },
  {
    words: ["to", "too", "two"],
    rule: "to = direction or infinitive (to the store, to write); too = also or excessively; two = the number 2.",
  },
  {
    words: ["loose", "lose"],
    rule: "loose = not tight (a loose knot); lose = to misplace or be defeated.",
  },
  {
    words: ["whose", "who's"],
    rule: "whose = possessive (whose idea); who's = who is or who has.",
  },
  {
    words: ["accept", "except"],
    rule: "accept = to receive or agree; except = excluding.",
  },
  {
    words: ["advice", "advise"],
    rule: "advice = the noun (good advice); advise = the verb (to advise someone).",
  },
  {
    words: ["complement", "compliment"],
    rule: "complement = completes or goes well with; compliment = praise.",
  },
  {
    words: ["principal", "principle"],
    rule: "principal = main, or a school head; principle = a rule or belief.",
  },
  {
    words: ["stationary", "stationery"],
    rule: "stationary = not moving; stationery = writing paper and supplies.",
  },
  {
    words: ["weather", "whether"],
    rule: "weather = rain, sun, wind; whether = introduces alternatives (whether or not).",
  },
  {
    words: ["cite", "site", "sight"],
    rule: "cite = to reference a source; site = a location; sight = vision or something seen.",
  },
  {
    words: ["capital", "capitol"],
    rule: "capital = a city, wealth, or uppercase letter; capitol = a legislative building.",
  },
  {
    words: ["desert", "dessert"],
    rule: "desert = dry land or to abandon; dessert = the sweet course (two s's, you want more).",
  },
  {
    words: ["council", "counsel"],
    rule: "council = a group that deliberates; counsel = advice, or to advise.",
  },
  {
    words: ["elicit", "illicit"],
    rule: "elicit = to draw out (elicit a response); illicit = illegal or forbidden.",
  },
  {
    words: ["eminent", "imminent"],
    rule: "eminent = distinguished; imminent = about to happen.",
  },
  {
    words: ["farther", "further"],
    rule: "farther = physical distance; further = figurative extent or degree.",
  },
  {
    words: ["fewer", "less"],
    rule: "fewer = countable things (fewer errors); less = uncountable quantities (less time).",
  },
  {
    words: ["imply", "infer"],
    rule: "the speaker implies (suggests); the listener infers (deduces).",
  },
  {
    words: ["lay", "lie"],
    rule: "lay takes an object (lay the book down); lie does not (lie down and rest).",
  },
  {
    words: ["lead", "led"],
    rule: "led = past tense of the verb lead; lead (rhyming with led) is the metal.",
  },
  {
    words: ["passed", "past"],
    rule: "passed = past tense of pass (she passed the exam); past = time gone by or beyond.",
  },
  {
    words: ["peace", "piece"],
    rule: "peace = calm, absence of war; piece = a portion (a piece of pie).",
  },
  {
    words: ["precede", "proceed"],
    rule: "precede = to come before; proceed = to continue or go forward.",
  },
  {
    words: ["quiet", "quite"],
    rule: "quiet = silent; quite = fairly or completely.",
  },
  {
    words: ["brake", "break"],
    rule: "brake = what stops a vehicle; break = to shatter, or a pause.",
  },
  {
    words: ["bare", "bear"],
    rule: "bare = uncovered; bear = to carry or endure, or the animal.",
  },
  {
    words: ["board", "bored"],
    rule: "board = a plank, a committee, or to get on; bored = uninterested.",
  },
  {
    words: ["allowed", "aloud"],
    rule: "allowed = permitted; aloud = out loud, audibly.",
  },
  {
    words: ["altar", "alter"],
    rule: "altar = a raised structure for worship; alter = to change.",
  },
  {
    words: ["ensure", "insure", "assure"],
    rule: "ensure = make certain; insure = cover with insurance; assure = tell someone confidently.",
  },
  {
    words: ["emigrate", "immigrate"],
    rule: "emigrate = to leave a country (exit); immigrate = to enter a new country (into).",
  },
  {
    words: ["everyday", "every day"],
    rule: "everyday = ordinary (an everyday routine); every day = each day (I write every day).",
  },
  {
    words: ["discreet", "discrete"],
    rule: "discreet = tactful, unobtrusive; discrete = separate and distinct.",
  },
  {
    words: ["device", "devise"],
    rule: "device = the noun (a gadget or method); devise = the verb (to invent or plan).",
  },
  {
    words: ["dual", "duel"],
    rule: "dual = double, twofold; duel = a fight between two people.",
  },
  {
    words: ["flair", "flare"],
    rule: "flair = natural talent or style; flare = a burst of light, or to widen.",
  },
  {
    words: ["hoard", "horde"],
    rule: "hoard = a hidden stockpile, or to stockpile; horde = a large crowd.",
  },
  {
    words: ["moral", "morale"],
    rule: "moral = ethical, or a lesson; morale = the spirit or confidence of a group.",
  },
  {
    words: ["personal", "personnel"],
    rule: "personal = private, one's own; personnel = staff, employees.",
  },
  {
    words: ["pour", "pore"],
    rule: "pour = to make liquid flow; pore = to study closely (pore over), or a skin opening.",
  },
  {
    words: ["waist", "waste"],
    rule: "waist = the middle of the body; waste = to squander, or refuse.",
  },
  {
    words: ["wander", "wonder"],
    rule: "wander = to roam; wonder = to ask yourself, or a marvel.",
  },
];

const ALL_CONFUSED_WORDS = CONFUSED_GROUPS.flatMap((g) => g.words).sort(
  (a, b) => b.length - a.length
);

const GROUP_BY_WORD = new Map<string, ConfusedGroup>(
  CONFUSED_GROUPS.flatMap((g) => g.words.map((w) => [w, g] as const))
);

const RE_CONFUSED = new RegExp(
  `\\b(?:${ALL_CONFUSED_WORDS.join("|").replace(/'/g, "['’]")})\\b`,
  "gi"
);
const RE_SPLIT_WORDS = /\s+/;
const RE_CURLY_APOSTROPHE = /’/g;

/* ------------------------------------------------------------------ */
/*  Analysis                                                          */
/* ------------------------------------------------------------------ */

interface GroupFinding {
  group: ConfusedGroup;
  counts: Map<string, number>;
  total: number;
}

interface AnalysisResult {
  wordCount: number;
  totalFlagged: number;
  findings: GroupFinding[];
  highlighted: ReactNode[];
}

function buildHighlighted(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(RE_CONFUSED)) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    nodes.push(
      <mark
        className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-900/60 dark:text-yellow-100"
        key={`confused-${match.index}`}
      >
        {match[0]}
      </mark>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
}

function collectFindings(text: string): GroupFinding[] {
  const byGroup = new Map<ConfusedGroup, Map<string, number>>();
  for (const match of text.matchAll(RE_CONFUSED)) {
    const word = match[0].toLowerCase().replace(RE_CURLY_APOSTROPHE, "'");
    const group = GROUP_BY_WORD.get(word);
    if (!group) {
      continue;
    }
    const counts = byGroup.get(group) ?? new Map<string, number>();
    counts.set(word, (counts.get(word) ?? 0) + 1);
    byGroup.set(group, counts);
  }
  return Array.from(byGroup.entries())
    .map(([group, counts]) => ({
      group,
      counts,
      total: Array.from(counts.values()).reduce((sum, n) => sum + n, 0),
    }))
    .sort((a, b) => b.total - a.total);
}

function analyzeText(text: string): AnalysisResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const findings = collectFindings(text);
  return {
    wordCount: trimmed.split(RE_SPLIT_WORDS).filter(Boolean).length,
    totalFlagged: findings.reduce((sum, f) => sum + f.total, 0),
    findings,
    highlighted: buildHighlighted(text),
  };
}

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const CONFUSED_WORDS_CHECKER_STRINGS = {
  textToCheckForCommonly: "Text to check for commonly confused words",
  pasteOrTypeYourText:
    "Paste or type your text here to check for commonly confused words...",
  totalWords: "Total Words",
  flaggedWords: "Flagged Words",
  pairsInvolved: "Pairs Involved",
  yourTextWithConfusableWords: "Your Text with Confusable Words Highlighted",
  pairsToVerify: "Pairs to Verify",
  eachCardShowsAConfused:
    "Each card shows a confused-word group found in your text and the rule that separates its members. Check every occurrence — the tool cannot tell which word you intended.",
  noConfusableWordsFound: "No confusable words found",
  noneOfTheCommonlyConfused:
    "None of the commonly confused words on our list appear in your text.",
} as const;

export default function ConfusedWordsCheckerWidget() {
  const [text, setText] = useState("");

  const analysis = useMemo(() => analyzeText(text), [text]);

  const strings = useToolUi(CONFUSED_WORDS_CHECKER_STRINGS);

  const fmt = useToolNumber();

  return (
    <>
      {/* Input */}
      <textarea
        aria-label={strings.textToCheckForCommonly}
        className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        onChange={(e) => setText(e.target.value)}
        placeholder={strings.pasteOrTypeYourText}
        value={text}
      />
      {/* Stats */}
      {analysis && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.totalWords}
            value={fmt(analysis.wordCount)}
          />
          <StatCard
            label={strings.flaggedWords}
            value={fmt(analysis.totalFlagged)}
          />
          <StatCard
            label={strings.pairsInvolved}
            value={fmt(analysis.findings.length)}
          />
        </div>
      )}
      {/* Highlighted text */}
      {analysis && analysis.totalFlagged > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.yourTextWithConfusableWords}
          </h2>
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {analysis.highlighted}
          </div>
        </section>
      )}
      {/* Findings grouped by pair */}
      {analysis && analysis.findings.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.pairsToVerify}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.eachCardShowsAConfused}
          </p>
          <div className="space-y-3">
            {analysis.findings.map((finding) => (
              <div
                className="rounded-lg border border-border bg-card p-4"
                key={finding.group.words.join("/")}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium text-foreground text-sm">
                    {finding.group.words.join(" / ")}
                  </span>
                  <span className="text-muted-foreground text-sm">
                    {Array.from(finding.counts.entries())
                      .map(([word, count]) => `${word} ×${count}`)
                      .join(", ")}
                  </span>
                </div>
                <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                  {finding.group.rule}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
      {/* None found */}
      {analysis && analysis.totalFlagged === 0 && (
        <div className="rounded-lg border border-border bg-card p-5 text-center">
          <p className="font-medium text-foreground">
            {strings.noConfusableWordsFound}
          </p>
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.noneOfTheCommonlyConfused}
          </p>
        </div>
      )}
    </>
  );
}
